import { Emitter, Event } from "../../../base/common/events.js";
import { createDecorator, registerSingleton } from "../../instantiation/common/registry.js";
import { SyncDescriptor } from "../../instantiation/common/descriptors.js";
import { IStorageService, StorageScope, StorageTarget } from "../../storage/common/storage.js";

/**
 * The stable star key for a property — its `metadata.contextKey`
 * (e.g. `material.roughness`, `object.position`, `renderer.<x>`).
 *
 * Chosen over a group-category+path key because it is derived from the PROPERTY
 * alone:
 * - category-embedded (`material.` / `object.` prefix) so paths don't collide,
 * - instance-INDEPENDENT (no session id / uuid) so starring once surfaces the
 *   property for every object of that shape ("star roughness → every material"),
 * - the SAME for a property drawn twice (pinned block + its original spot),
 *   which the group-threaded key could not guarantee.
 *
 * Returns undefined when the property has no `contextKey` — in v1 only such
 * (curated, top-level) properties are starrable.
 */
export function starKeyForProperty(
    property: { metadata?: { contextKey?: unknown } } | null | undefined,
): string | undefined {
    const key = property?.metadata?.contextKey;
    return typeof key === "string" && key.length > 0 ? key : undefined;
}

/**
 * Free tier: unlicensed users may pin up to this many properties, then the star
 * opens the Pro upsell. A genuine teaser — 3 GLOBAL pins is enough to feel the
 * "it follows me across objects" hook, far too small to be a real workspace, so
 * the gate still bites for anyone who adopts it. Licensed users are unlimited.
 */
export const FREE_STAR_LIMIT = 3;

/**
 * Whether an unlicensed user is allowed to perform this star toggle under the
 * free cap. Enforcement lives at the UI gate (which knows the license state);
 * this is the pure rule so it can be tested and can't drift:
 * - UNstarring is ALWAYS allowed (frees a slot; never destroys the user's data,
 *   even if they somehow hold more than the limit after a lapsed license),
 * - starring a NEW property is allowed only while under the limit.
 * The service itself stays license-agnostic — licensed callers skip this.
 */
export function isStarToggleAllowedForFreeTier(args: {
    currentCount: number;
    isCurrentlyStarred: boolean;
    limit?: number;
}): boolean {
    if (args.isCurrentlyStarred) return true; // unstar always OK
    return args.currentCount < (args.limit ?? FREE_STAR_LIMIT);
}

export const IStarredPropertyService = createDecorator<IStarredPropertyService>("starredPropertyService");

export interface IStarredPropertyService {
    readonly _serviceBrand: undefined;
    /** Fires whenever the starred set changes (star / unstar). */
    readonly onDidChange: Event<void>;
    /** Starred keys in pin order — insertion order in v1; user-reorderable later. */
    readonly starred: readonly string[];
    isStarred(key: string): boolean;
    /** Star if not starred, unstar if starred. */
    toggle(key: string): void;
    star(key: string): void;
    unstar(key: string): void;
}

/** Single APPLICATION-scoped, machine-targeted entry holding the JSON key list —
 *  same persistence shape as the inspector's other view prefs (selectedCategory,
 *  per-property expanded). It's a USER VIEW preference, never a scene edit, so it
 *  does NOT flow through the command/undo seam. */
const STORAGE_KEY = "inspector.starredProperties";

export class StarredPropertyService implements IStarredPropertyService {
    declare readonly _serviceBrand: undefined;

    private readonly _onDidChange = new Emitter<void>();
    readonly onDidChange = this._onDidChange.event;

    // Ordered array (a Set would lose pin order); membership via includes().
    private _starred: string[] = [];

    constructor(
        @IStorageService private readonly storage: IStorageService,
    ) {
        this._starred = this._load();
    }

    get starred(): readonly string[] { return this._starred; }

    isStarred(key: string): boolean {
        return this._starred.includes(key);
    }

    toggle(key: string): void {
        if (this.isStarred(key)) this.unstar(key);
        else this.star(key);
    }

    star(key: string): void {
        if (this.isStarred(key)) return; // no-op, no event
        this._starred = [...this._starred, key];
        this._persist();
    }

    unstar(key: string): void {
        if (!this.isStarred(key)) return; // no-op, no event
        this._starred = this._starred.filter(k => k !== key);
        this._persist();
    }

    private _persist(): void {
        this.storage.store(STORAGE_KEY, JSON.stringify(this._starred), StorageScope.APPLICATION, StorageTarget.MACHINE);
        this._onDidChange.fire();
    }

    private _load(): string[] {
        const raw = this.storage.get(STORAGE_KEY, StorageScope.APPLICATION);
        if (!raw) return [];
        try {
            const parsed = JSON.parse(raw);
            return Array.isArray(parsed) ? parsed.filter((k): k is string => typeof k === "string") : [];
        } catch {
            return []; // corrupt persisted value — start clean rather than throw
        }
    }
}

// Lazy singleton (like other property-domain services). Imported by the property
// provider so the registration side-effect runs and the container can resolve it.
registerSingleton(IStarredPropertyService, new SyncDescriptor(StarredPropertyService, [], false));
